import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Upload, X } from 'lucide-react';
import {
  useCreateSkill,
  useParseSkillZip,
  useSkill,
  useUpdateSkill,
} from '@/features/skills/hooks';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/lib/api-client';

interface FormVals {
  name: string;
  description: string;
  howToUse: string;
  keyPoints: string;
  tags: string; // CSV no input
}

export default function SkillFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const nav = useNavigate();
  const toast = useToast();

  const existing = useSkill(id);
  const parse = useParseSkillZip();
  const create = useCreateSkill();
  const update = useUpdateSkill();

  const [file, setFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const { register, handleSubmit, setValue, watch } = useForm<FormVals>({
    defaultValues: { name: '', description: '', howToUse: '', keyPoints: '', tags: '' },
  });

  useEffect(() => {
    if (isEdit && existing.data) {
      const s = existing.data;
      setValue('name', s.name);
      setValue('description', s.description);
      setValue('howToUse', s.howToUse);
      setValue('keyPoints', s.keyPoints);
      setValue('tags', s.tags.map((t) => t.name).join(', '));
    }
  }, [isEdit, existing.data, setValue]);

  const handleFile = async (f: File | null) => {
    setFile(f);
    if (!f) return;
    try {
      const parsed = await parse.mutateAsync(f);
      // Só pré-preenche se os campos estiverem vazios ou se for criação
      if (!isEdit || !watch('name')) setValue('name', parsed.name);
      if (!isEdit || !watch('description')) setValue('description', parsed.description);
      if (!isEdit || !watch('howToUse')) setValue('howToUse', parsed.suggestedHowToUse);
      if (!isEdit || !watch('keyPoints')) setValue('keyPoints', parsed.suggestedKeyPoints);
      toast.success('Metadata extraída do SKILL.md.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao parsear o zip.'));
    }
  };

  const onSubmit = async (v: FormVals) => {
    setFormError(null);
    const tagsArr = v.tags
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

    try {
      if (isEdit) {
        const s = await update.mutateAsync({
          id: id!,
          zip: file ?? undefined,
          name: v.name,
          description: v.description,
          howToUse: v.howToUse,
          keyPoints: v.keyPoints,
          tags: tagsArr,
        });
        toast.success('Skill atualizada.');
        nav(`/skills/${s.id}`);
      } else {
        if (!file) {
          setFormError('Selecione um arquivo .zip antes de salvar.');
          return;
        }
        const s = await create.mutateAsync({
          zip: file,
          name: v.name,
          description: v.description,
          howToUse: v.howToUse,
          keyPoints: v.keyPoints,
          tags: tagsArr,
        });
        toast.success('Skill criada.');
        nav(`/skills/${s.id}`);
      }
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Falha ao salvar a skill.'));
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold">{isEdit ? 'Editar skill' : 'Nova skill'}</h1>
          <p className="text-sm text-text-muted">
            {isEdit
              ? 'Edite os campos e (opcionalmente) substitua o arquivo .zip para criar uma nova versão.'
              : 'Arraste o .zip da skill (com SKILL.md na raiz ou em uma subpasta única). Os campos serão pré-preenchidos pra você revisar.'}
          </p>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Dropzone
              file={file}
              onFile={handleFile}
              loading={parse.isPending}
              optional={isEdit}
            />
            <Input label="Nome" {...register('name', { required: true })} />
            <Textarea label="Descrição curta" {...register('description', { required: true })} />
            <Textarea
              label="Como usar (markdown)"
              {...register('howToUse', { required: true })}
              className="min-h-[200px]"
            />
            <Textarea
              label="Principais pontos (markdown, opcional)"
              {...register('keyPoints')}
              className="min-h-[120px]"
            />
            <Input
              label="Tags"
              {...register('tags')}
              hint="Separe por vírgula. Apenas letras, números e hífens. Ex: seguranca, tdd"
            />

            {formError && <p className="text-sm text-red-400">{formError}</p>}

            <div className="flex gap-3 justify-end">
              <Button variant="ghost" type="button" onClick={() => nav(-1)}>
                Cancelar
              </Button>
              <Button type="submit" loading={create.isPending || update.isPending}>
                {isEdit ? 'Salvar alterações' : 'Criar skill'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function Dropzone({
  file,
  onFile,
  loading,
  optional,
}: {
  file: File | null;
  onFile: (f: File | null) => void;
  loading?: boolean;
  optional?: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={
        'border-2 border-dashed rounded-xl p-6 text-center transition ' +
        (over ? 'border-brand-400 bg-brand-500/10' : 'border-border bg-surface-base')
      }
    >
      {file ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Badge tone="brand">.zip</Badge>
            <span className="text-sm">{file.name}</span>
            <span className="text-xs text-text-muted">
              {(file.size / 1024).toFixed(1)} KB
            </span>
          </div>
          <button
            type="button"
            onClick={() => onFile(null)}
            className="text-text-muted hover:text-text-primary"
            aria-label="Remover"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <label className="cursor-pointer flex flex-col items-center gap-2">
          <Upload size={24} className="text-text-muted" />
          <span className="text-sm text-text-secondary">
            Arraste o .zip aqui ou clique para selecionar{optional ? ' (opcional)' : ''}
          </span>
          {loading && <span className="text-xs text-text-muted">Lendo SKILL.md...</span>}
          <input
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
        </label>
      )}
    </div>
  );
}
